'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Box, ThemeProvider } from '@mui/material';
import { scrollSize } from '@/constants/scroll.style.constants';
import { isWindowAgent } from '@/utils/browser.util';
import FlexBox from '@/components/FlexBox';
import { DESKTOP_CONTENTS_MAX_WIDTH, DESKTOP_HEADER_HEIGHT, MOBILE_HEADER_HEIGHT, MOBILE_MIN_WIDTH } from '@/app/stylesguides';
import theme from '@/app/theme';
import useCheckIsMobileSize from '@/hooks/useCheckIsMobileSize';
import useCheckisSmallerBaseDeskTopSize from '@/hooks/useCheckisSmallerBaseDeskTopSize';

const ThemeProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();

  const isIntroPage = pathname === '/';
  const isAUthPage = pathname?.includes('/login') || pathname?.includes('/signup');
  const isLandingPage = pathname?.includes('/landing');
  const isPaddingNone = isAUthPage || isLandingPage;
  const isReviewBoostPage = pathname?.includes('/review-boost');

  const isMobile = useCheckIsMobileSize();
  const paddingTop = isReviewBoostPage ? '0px' : `${isMobile ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT}px`;
  const isSmallerBaseDeskTop = useCheckisSmallerBaseDeskTopSize();

  const [isWindowOS, setIsWindowOS] = useState(false);

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setIsWindowOS(isWindowAgent(navigator.userAgent));
    }
  }, []);

  const scrollStyle = isWindowOS
    ? {
        scrollbarGutter: isIntroPage ? 'auto' : 'stable',
        '::-webkit-scrollbar': scrollSize,
        '::-webkit-scrollbar-button:vertical:start:decrement': {
          display: 'block',
          height: `${isMobile ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT}px`
        }
      }
    : {};

  return (
    <ThemeProvider theme={theme}>
      <Box
        component="main"
        sx={{
          position: 'relative',
          width: '100%',
          minWidth: `${MOBILE_MIN_WIDTH}px`,
          overflowX: 'hidden',
          backgroundColor: isIntroPage ? 'white' : theme.palette.blue[50],
          height: isIntroPage ? '100%' : '100vh',
          paddingTop: paddingTop,
          paddingRight: 0,
          ...scrollStyle
        }}
      >
        {isIntroPage ? (
          children
        ) : (
          <FlexBox
            style={{
              marginX: 'auto',
              width: '100%',
              maxWidth: isLandingPage ? 'auto' : DESKTOP_CONTENTS_MAX_WIDTH,
              height: '100%',
              flexDirection: 'column',
              justifyContent: 'start',
              paddingX: isSmallerBaseDeskTop && !isMobile && !isPaddingNone ? '20px' : 0
            }}
          >
            {children}
          </FlexBox>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ThemeProviderWrapper;
